import { baseAPI } from '~shared/api/base-api';

import type {
  AudiencePreview,
  CampaignRecipientsResponse,
  EmailCampaign,
  EmailCampaignTemplate,
  EmailCampaignTemplateCategory,
  EmailComposerDefaults,
  UnlayerTemplateListItem,
  UnlayerUserIdentity,
} from '../model/types';

export const getComposerDefaultsAPI =
  async (): Promise<EmailComposerDefaults> => {
    const resp = await baseAPI.get<EmailComposerDefaults>(
      'email-composer/defaults',
    );
    return resp.data;
  };

export const updateComposerDefaultsAPI = async (
  body: Partial<EmailComposerDefaults>,
): Promise<EmailComposerDefaults> => {
  const resp = await baseAPI.put<EmailComposerDefaults>(
    'email-composer/defaults',
    body,
  );
  return resp.data;
};

export const listCampaignTemplatesAPI = async (
  category?: string,
): Promise<Array<EmailCampaignTemplate>> => {
  const resp = await baseAPI.get<Array<EmailCampaignTemplate>>(
    'email-campaign-templates',
    { params: category ? { category } : undefined },
  );
  return resp.data;
};

export const getCampaignTemplateAPI = async (
  id: string,
): Promise<EmailCampaignTemplate> => {
  const resp = await baseAPI.get<EmailCampaignTemplate>(
    `email-campaign-templates/${id}`,
  );
  return resp.data;
};

export const createCampaignTemplateAPI = async (body: {
  name: string;
  category?: EmailCampaignTemplateCategory;
  designJson: Record<string, unknown>;
  htmlSnapshot?: string;
  thumbnailUrl?: string;
  audience?: EmailCampaignTemplate['audience'];
  scheduleAt?: string | null;
}): Promise<EmailCampaignTemplate> => {
  const resp = await baseAPI.post<EmailCampaignTemplate>(
    'email-campaign-templates',
    body,
  );
  return resp.data;
};

export const createTemplateFromUnlayerAPI = async (body: {
  unlayerTemplateId: string;
  name?: string;
  category?: EmailCampaignTemplateCategory;
}): Promise<EmailCampaignTemplate> => {
  const resp = await baseAPI.post<EmailCampaignTemplate>(
    'email-campaign-templates/from-unlayer',
    body,
  );
  return resp.data;
};

export const duplicateCampaignTemplateAPI = async (
  id: string,
): Promise<EmailCampaignTemplate> => {
  const resp = await baseAPI.post<EmailCampaignTemplate>(
    `email-campaign-templates/${id}/duplicate`,
  );
  return resp.data;
};

export const updateCampaignTemplateAPI = async (
  id: string,
  body: Partial<{
    name: string;
    category: EmailCampaignTemplateCategory;
    designJson: Record<string, unknown>;
    htmlSnapshot: string;
    thumbnailUrl: string;
    audience: EmailCampaignTemplate['audience'];
    scheduleAt: string | null;
  }>,
): Promise<EmailCampaignTemplate> => {
  const resp = await baseAPI.patch<EmailCampaignTemplate>(
    `email-campaign-templates/${id}`,
    body,
  );
  return resp.data;
};

export const deleteCampaignTemplateAPI = async (
  id: string,
): Promise<{ success: boolean }> => {
  const resp = await baseAPI.delete<{ success: boolean }>(
    `email-campaign-templates/${id}`,
  );
  return resp.data;
};

export const listCampaignsAPI = async (
  status?: string,
  templateId?: string,
): Promise<Array<EmailCampaign>> => {
  const resp = await baseAPI.get<Array<EmailCampaign>>('email-campaigns', {
    params: {
      ...(status ? { status } : {}),
      ...(templateId ? { templateId } : {}),
    },
  });
  return resp.data;
};

export const getCampaignAPI = async (id: string): Promise<EmailCampaign> => {
  const resp = await baseAPI.get<EmailCampaign>(`email-campaigns/${id}`);
  return resp.data;
};

export const createCampaignAPI = async (body: {
  subject?: string;
  designJson?: Record<string, unknown> | null;
  html?: string;
  audience?: {
    tagIds?: Array<string>;
    classIds?: Array<string>;
    eventIds?: Array<string>;
    excludeParentIds?: Array<string>;
  };
  settingsOverride?: EmailCampaign['settingsOverride'];
  templateId?: string;
  scheduleAt?: string | null;
}): Promise<EmailCampaign> => {
  const resp = await baseAPI.post<EmailCampaign>('email-campaigns', body);
  return resp.data;
};

export const updateCampaignAPI = async (
  id: string,
  body: Partial<{
    subject: string;
    designJson: Record<string, unknown> | null;
    html: string;
    audience: {
      tagIds?: Array<string>;
      classIds?: Array<string>;
      eventIds?: Array<string>;
      excludeParentIds?: Array<string>;
    };
    settingsOverride: EmailCampaign['settingsOverride'];
    templateId: string | null;
    scheduleAt: string | null;
  }>,
): Promise<EmailCampaign> => {
  const resp = await baseAPI.patch<EmailCampaign>(
    `email-campaigns/${id}`,
    body,
  );
  return resp.data;
};

export const previewAudienceAPI = async (
  id: string,
): Promise<AudiencePreview> => {
  const resp = await baseAPI.post<AudiencePreview>(
    `email-campaigns/${id}/audience/preview`,
  );
  return resp.data;
};

export const confirmScheduleAPI = async (
  id: string,
  scheduleAt: string,
  subject?: string,
): Promise<EmailCampaign> => {
  const resp = await baseAPI.post<EmailCampaign>(
    `email-campaigns/${id}/confirm-schedule`,
    { scheduleAt, subject },
  );
  return resp.data;
};

export const unlockScheduleAPI = async (
  id: string,
): Promise<EmailCampaign> => {
  const resp = await baseAPI.post<EmailCampaign>(
    `email-campaigns/${id}/unlock-schedule`,
  );
  return resp.data;
};

export const cancelCampaignAPI = async (
  id: string,
): Promise<EmailCampaign> => {
  const resp = await baseAPI.post<EmailCampaign>(
    `email-campaigns/${id}/cancel`,
  );
  return resp.data;
};

export const listCampaignRecipientsAPI = async (
  id: string,
  params?: { page?: number; limit?: number; status?: string },
): Promise<CampaignRecipientsResponse> => {
  const resp = await baseAPI.get<CampaignRecipientsResponse>(
    `email-campaigns/${id}/recipients`,
    { params },
  );
  return resp.data;
};

export const getUnlayerIdentityAPI = async (): Promise<UnlayerUserIdentity> => {
  const resp = await baseAPI.get<UnlayerUserIdentity>('unlayer/identity');
  return resp.data;
};

export const listUnlayerTemplatesAPI = async (params?: {
  page?: number;
  perPage?: number;
  includeDesign?: number;
}): Promise<{ success?: boolean; data: Array<UnlayerTemplateListItem> }> => {
  const resp = await baseAPI.get<{
    success?: boolean;
    data: Array<UnlayerTemplateListItem>;
  }>('unlayer/templates', { params });
  return resp.data;
};

export const exportUnlayerHtmlAPI = async (body: {
  design: Record<string, unknown>;
  displayMode: 'email' | 'web' | 'popup';
}): Promise<{ success?: boolean; data?: { html?: string } }> => {
  const resp = await baseAPI.post<{
    success?: boolean;
    data?: { html?: string };
  }>('unlayer/export/html', body);
  return resp.data;
};
